import styled, { keyframes } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../components/AppShell/styles";

const pulse = keyframes`
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;

  /* Mobile: one 20px rhythm for the page padding and the space between blocks */
  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    gap: 20px;
    padding: 20px;
  }
`;

/*
 * Informational block: same solid fill as the action cards below. The address boxes inside use a
 * lighter gray to stay visible on it.
 */
export const Hero = styled.div`
  --hero-pill-bg: var(--color-neutral-base);

  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--color-neutral-dark);
  border-radius: 12px;

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    padding: 20px;
  }
`; /*
 * Mobile: fixed-size items (plan tag, active toggle) share the first row and the title,
 * whose length depends on the user, gets its own row below.
 * Desktop: everything on a single row.
 */
export const HeroTop = styled.div`
  display: grid;
  grid-template-areas:
    "tag toggle"
    "title title";
  grid-template-columns: 1fr auto;
  gap: 0.75rem 1rem;
  align-items: center;

  .title {
    grid-area: title;
    display: -webkit-box;
    min-width: 0;
    overflow: hidden;
    font-size: 1.25rem;
    line-height: 1.75rem;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .tag {
    display: flex;
    grid-area: tag;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    grid-template-areas: "title tag . toggle";
    grid-template-columns: minmax(0, auto) auto 1fr auto;

    .title {
      display: block;
      font-size: 1.75rem;
      line-height: 2.25rem;
      -webkit-line-clamp: unset;
    }
  }
`;

export const ActiveToggle = styled.div`
  grid-area: toggle;
  display: flex;
  flex-shrink: 0;
  gap: 0.75rem;
  align-items: center;

  > span {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 24px;
    color: var(--color-neutral-white);
  }

  /* ToggleSwitch ships with a bottom margin and a text line box taller than its 24px track */
  > label {
    height: 24px;
    margin: 0;
    font-size: 0;
    line-height: 0;
  }
`;

/*
 * Mobile: the URL (any length) shrinks with ellipsis and Copy/Open are icon-only buttons
 * beside it. Desktop: URL followed by text actions.
 */
/* One address of the Keepo in its own light box: the address on the left, round icon buttons on the right */
export const LinkLine = styled.div`
  display: flex;
  gap: 0;
  max-width: 100%;
  align-items: center;
  padding: 3px 6px 3px 16px;
  background: var(--hero-pill-bg, var(--color-neutral-dark));
  border-radius: 100px;

  /* Address and its optional tag share the free width; the address ellipsizes */
  .address {
    display: flex;
    flex: 0 1 auto;
    gap: 0.5rem;
    align-items: center;
    min-width: 0;
    margin-right: 0.5rem;
  }

  .url {
    min-width: 0;
    overflow: hidden;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    color: var(--color-neutral-150);
    border-radius: 100%;
    transition: color 0.15s;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      color: var(--color-neutral-white);
    }

    &.copied {
      color: var(--color-brand-plus);
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.4;

      &:hover {
        color: var(--color-neutral-150);
      }
    }

    /* Icons only; the name is in aria-label and title */
    .label {
      display: none;
    }
  }
`;

/* Compact shortcuts: always two per row */
export const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;

  /* Desktop: three per row, room for the shortcuts still to come */
  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    gap: 12px;
  }
`;

export const ActionCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  width: 100%;
  padding: 1rem;
  text-align: left;
  background: var(--color-neutral-dark);
  border: 1px solid transparent;
  border-radius: 12px;
  transition:
    border-color 0.15s,
    transform 0.1s;

  /* Chevron: signals the card navigates somewhere */
  &::after {
    position: absolute;
    top: 0.75rem;
    right: 1rem;
    font-size: 1.5rem;
    line-height: 1;
    color: var(--color-neutral-150);
    content: "›";
  }

  &:hover {
    border-color: var(--color-neutral-light);
  }

  &:active {
    transform: scale(0.98);
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 100%;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  h4 {
    font-size: 0.9375rem;
    line-height: 1.25rem;
  }

  /* PLUS-only shortcut on a FREE Keepo: tag beside the chevron */
  .plus-tag {
    position: absolute;
    top: 1rem;
    right: 2.25rem;
  }

  /* Phones, three per row: centered and compact, no chevron; long labels hyphenate instead of overflowing */
  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    gap: 0.5rem;
    align-items: center;
    padding: 0.875rem 0.375rem;
    text-align: center;

    &::after {
      display: none;
    }

    h4 {
      width: 100%;
      font-size: 0.75rem;
      line-height: 1rem;
      overflow-wrap: break-word;
      hyphens: auto;
    }

    .plus-tag {
      top: 0.375rem;
      right: 0.375rem;
    }
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      border-color: transparent;
    }

    &:active {
      transform: none;
    }
  }
`;

/* PLUS Keepos get their benefits here instead of the promo: same green glow, so it reads as the plan */
export const PlusBenefitsCard = styled.button`
  /* Phones: tag on the left, the usage (title + bar) in the middle, chevron on the right. Desktop: one
     compact row (tag, title, usage count with its bar, chevron) */
  display: grid;
  grid-template-areas: "tag body chevron";
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: center;
  width: 100%;
  padding: 1rem 1.25rem;
  text-align: left;
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(87, 242, 139, 0.2) 0%, rgba(87, 242, 139, 0) 60%),
    var(--color-neutral-dark);
  border-radius: 1rem;
  box-shadow: inset 0 0 0 1px rgba(87, 242, 139, 0.25);
  transition: filter 0.15s;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    grid-template-areas: "tag body usage chevron";
    grid-template-columns: auto minmax(0, 1fr) 280px auto;
    padding: 0.75rem 1rem;
    border-radius: 12px;

    h4 {
      font-size: 0.875rem;
    }
  }

  &:hover {
    filter: brightness(1.1);
  }

  .tag {
    grid-area: tag;
    justify-self: start;
  }

  /* As wide as the title, so the bar under it ends where the title does */
  .body {
    display: inline-flex;
    flex-direction: column;
    grid-area: body;
    gap: 0.375rem;
    justify-self: start;
    max-width: 100%;
  }

  h4 {
    font-size: 0.9375rem;
    line-height: 1.25rem;
  }

  .usage {
    display: flex;
    grid-area: usage;
    gap: 0.625rem;
    align-items: center;
    white-space: nowrap;

    .progress {
      flex: 1;
      min-width: 48px;
      max-width: 160px;
    }
  }

  .count {
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  .progress {
    width: 100%;
    height: 4px;
    overflow: hidden;
    background: var(--color-neutral-base);
    border-radius: 100px;

    span {
      display: block;
      height: 100%;
      background: var(--color-brand-plus);
      border-radius: inherit;
    }
  }

  .chevron {
    display: flex;
    grid-area: chevron;
    color: var(--color-brand-plus);
  }

  /* Phones: a smaller usage title, centered with its bar */
  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    .body {
      align-items: center;
      justify-self: center;
      text-align: center;
    }

    h4 {
      font-size: 0.8125rem;
      line-height: 1.125rem;
    }
  }

  /* Last, so they win over the display of the blocks above */
  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    .mobile-only {
      display: none;
    }
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    .desktop-only {
      display: none;
    }
  }
`;

/* The Keepo's addresses, one row each (custom domain first), plus the placeholder while it loads */
export const Links = styled.div`
  /* Each address box is as wide as its content; they sit side by side and wrap when they don't fit */
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  /* Same box as an address row, about as wide as the "connect domain" box it usually turns into */
  .skeleton {
    display: flex;
    align-items: center;
    width: 160px;
    max-width: 100%;
    height: 34px;
    padding: 0 16px;
    background: var(--hero-pill-bg, var(--color-neutral-dark));
    border-radius: 100px;

    span {
      display: block;
      width: 100%;
      height: 12px;
      background: var(--color-neutral-base);
      border-radius: 100px;
      animation: ${pulse} 1.2s ease-in-out infinite;
    }
  }

  /* No custom domain yet: a dashed box right after the Keepo address, same shape as the address boxes */
  .connect-domain {
    display: inline-flex;
    align-items: center;
    height: 34px;
    padding: 0 16px;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 400;
    /* Grey: an option, not something asking for attention */
    color: var(--color-neutral-150);
    border: 1px dashed var(--color-neutral-base);
    border-radius: 100px;
    transition:
      color 0.15s,
      background-color 0.15s,
      border-color 0.15s;

    &:hover {
      color: var(--color-neutral-white);
      background: rgba(255, 255, 255, 0.04);
      border-color: var(--color-neutral-light);
    }
  }
`;
